import React, { useState, useEffect } from "react";
import { ChevronUp } from "lucide-react";

interface ScrollToTopProps {
  showOffset?: number;
  smooth?: boolean;
  className?: string;
}

const ScrollToTopButton: React.FC<ScrollToTopProps> = ({
  showOffset = 300,
  smooth = true,
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > showOffset) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, [showOffset]);

  const scrollToTop = () => {
    if (smooth) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <button
      onClick={scrollToTop}
      className={`
        fixed bottom-8 right-8 z-50
        w-12 h-12 
        bg-primary
        hover:bg-linear-to-r hover:from-primary-dark hover:via-primary hover:to-primary-light
        text-white rounded-full
        flex items-center justify-center
        transition-all duration-300 ease-out
        shadow-primary hover:shadow-glow
        transform hover:-translate-y-1 hover:scale-105
        active:scale-95 active:translate-y-0
        ring-2 ring-primary-dark/50 hover:ring-primary-dark
        backdrop-blur-sm
        overflow-hidden
        origin-center
        before:absolute before:inset-0 before:bg-white before:opacity-0 
        before:transition-opacity before:duration-300 hover:before:opacity-10
        ${
          isVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4 pointer-events-none"
        }
        ${className}
      `}
      title="Scroll to top"
      aria-label="Scroll to top"
    >
      <ChevronUp
        size={20}
        className="transition-transform duration-300 group-hover:scale-110"
      />

      {/* Animated shimmer effect */}
      <div className="absolute inset-0 -top-2 -bottom-2 bg-linear-to-r from-transparent via-primary-50/30 to-transparent opacity-0 hover:opacity-20 transform -skew-x-12 transition-all duration-700 group-hover:translate-x-full animate-shimmer"></div>
    </button>
  );
};

export default ScrollToTopButton;
